/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Inject, Injectable } from '@angular/core';

import { Observable, of as observableOf } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

import { NbAuthResult } from './auth-result';
import { NbTokenService } from './token/token.service';
import { NbAuthToken, nbAuthCreateToken, NbAuthSimpleToken } from './token/token';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConstants } from '../../../common/variable';
import { ApiResponse } from '../../../common/model/ApiResponse';

const ownerStrategyName = 'strategy';

@Injectable()
export class NbAuthService {

  constructor(
    private httpClient: HttpClient,
    // protected tokenStorage: NbTokenStorage,
    protected tokenService: NbTokenService,
  ) { }

  /**
   * Retrieves current authenticated token stored
   * @returns {Observable<any>}
   */
  getToken(): Observable<NbAuthToken> {
    // return this.tokenService.get();
    return observableOf(null);
  }

  /**
   * Returns true if auth token is present in the token storage
   * @returns {Observable<boolean>}
   */
  isAuthenticated(): Observable<boolean> {
    return this.getToken()
      .pipe(map((token: NbAuthToken) => token.isValid()));
  }

  /**
   * Returns true if valid auth token is present in the token storage.
   * If not, calls the strategy refreshToken, and returns isAuthenticated() if success, false otherwise
   * @returns {Observable<boolean>}
   */
  isAuthenticatedOrRefresh(): Observable<boolean> {
    return this.getToken()
      .pipe(
        switchMap(token => {
          if (token.getValue() && !token.isValid()) {
            return this.refreshToken(token.getOwnerStrategyName(), token)
              .pipe(
                switchMap(res => {
                  if (res.isSuccess()) {
                    return this.isAuthenticated();
                  } else {
                    return observableOf(false);
                  }
                }),
              );
          } else {
            return observableOf(token.isValid());
          }
        }));
  }

  /**
   * Returns tokens stream
   * @returns {Observable<NbAuthSimpleToken>}
   */
  onTokenChange(): Observable<NbAuthToken> {
    return observableOf(null);
    // return this.tokenService.tokenChange();
  }

  /**
   * Returns authentication status stream
   * @returns {Observable<boolean>}
   */
  onAuthenticationChange(): Observable<boolean> {
    return this.onTokenChange()
      .pipe(map((token: NbAuthToken) => token.isValid()));
  }

  /**
   * Authenticates with the selected strategy
   * Stores received token in the token storage
   *
   * Example:
   * authenticate('email', {email: 'email@example.com', password: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  authenticate(strategyName: string, data?: any): Observable<NbAuthResult> {
    return observableOf(null);
    // return this.getStrategy(strategyName).authenticate(data)
    //   .pipe(
    //     switchMap((result: NbAuthResult) => {
    //       return this.processResultToken(result);
    //     }),
    //   );
  }

  doktarAuthenticate(data?: any): Observable<NbAuthResult> {
    const headers = new HttpHeaders()
      .set('campaignId', AppConstants.App.campaignId)
      .set('companyId', AppConstants.App.companyId)
      .set('roleId', AppConstants.App.roleId)
      ;

    return <Observable<NbAuthResult>>this.httpClient.post(AppConstants.Services.ApiService + '/api/login', data, { headers })
      .pipe(
        map((mapped: any) => {
          if (mapped.Data) {
            const token = nbAuthCreateToken(NbAuthSimpleToken, mapped.Data.Token, ownerStrategyName);
            // this.tokenStorage.set(token);
            this.tokenService.set(token);
            localStorage.setItem(AppConstants.Storage.User, JSON.stringify(mapped.Data));
            AppConstants.User = mapped.Data;
            return mapped;
          }
          return mapped;
        })
      );
    // .pipe(
    //   map((res: any) => res.json(),
    //     catchError((res: Response) => {
    //       // console.log(res);
    //       return observableOf(null); // this.onRequestError(res);
    //     })
    //   )
    // )
  }

  /**
   * Registers with the selected strategy
   * Stores received token in the token storage
   *
   * Example:
   * register('email', {email: 'email@example.com', name: 'Some Name', password: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  register(strategyName: string, data?: any): Observable<NbAuthResult> {
    return observableOf(null);
    // return this.getStrategy(strategyName).register(data)
    //   .pipe(
    //     switchMap((result: NbAuthResult) => {
    //       return this.processResultToken(result);
    //     }),
    //   );
  }

  /**
   * Sign outs with the selected strategy
   * Removes token from the token storage
   *
   * Example:
   * logout('email')
   *
   * @param strategyName
   * @returns {Observable<NbAuthResult>}
   */
  logout(strategyName: string): Observable<NbAuthResult> {
    localStorage.removeItem(AppConstants.Storage.User);
    return this.tokenService.clear();
    // .pipe(map(() => result));
    // return observableOf(result);

    // return this.getStrategy(strategyName).logout()
    //   .pipe(
    //     switchMap((result: NbAuthResult) => {
    //       if (result.isSuccess()) {
    //         this.tokenService.clear()
    //           .pipe(map(() => result));
    //       }
    //       return observableOf(result);
    //     }),
    //   );
  }

  /**
   * Sends forgot password request to the selected strategy
   *
   * Example:
   * requestPassword('email', {email: 'email@example.com'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  requestPassword(strategyName: string, data?: any): Observable<NbAuthResult> {
    return observableOf(null);
    // return this.getStrategy(strategyName).requestPassword(data);
  }

  /**
   * Tries to reset password with the selected strategy
   *
   * Example:
   * resetPassword('email', {newPassword: 'test'})
   *
   * @param strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  resetPassword(strategyName: string, data?: any): Observable<NbAuthResult> {
    // return this.getStrategy(strategyName).resetPassword(data);
    return observableOf(null);
  }

  /**
   * Sends a refresh token request
   * Stores received token in the token storage
   *
   * Example:
   * refreshToken('email', {token: token})
   *
   * @param {string} strategyName
   * @param data
   * @returns {Observable<NbAuthResult>}
   */
  refreshToken(strategyName: string, data?: any): Observable<NbAuthResult> {
    return observableOf(null);
    // return this.getStrategy(strategyName).refreshToken(data)
    //   .pipe(
    //     switchMap((result: NbAuthResult) => {
    //       return this.processResultToken(result);
    //     }),
    //   );
  }

  /**
   * Get registered strategy by name
   *
   * Example:
   * getStrategy('email')
   *
   * @param {string} provider
   * @returns {NbAbstractAuthProvider}
   */
  // protected getStrategy(strategyName: string): NbAuthStrategy {
  //   const found = this.strategies.find((strategy: NbAuthStrategy) => strategy.getName() === strategyName);

  //   if (!found) {
  //     throw new TypeError(`There is no Auth Strategy registered under '${strategyName}' name`);
  //   }

  //   return found;
  // }

  private processResultToken(result: NbAuthResult) {
    if (result.isSuccess() && result.getToken()) {
      return observableOf(null);
      // return this.tokenService.set(result.getToken())
      //   .pipe(
      //     map((token: NbAuthToken) => {
      //       return result;
      //     }),
      //   );
    }

    return observableOf(result);
  }

  checkAppStatus() {
    return this.httpClient.get<ApiResponse<any>>(AppConstants.Services.ApiService + `/subscription/appstatus`);
  }
}
